import React from 'react'

import { Box, Button, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessPendingReviewersDocument,
  SendRemindersPerformanceReviewProcessDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'
import moment from 'moment'

import Modal from 'modules/core/components/Modal'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { customRelativeTime } from 'modules/one_on_ones/lib/customRelativeTime'
import { useFiltersValues } from 'modules/performance_process/components/review/show/Filters/context'

import PendingReviewersDataTable from './PendingReviewersDataTable'

export const SendRemindersPanel: React.FC<{
  reviewId: number
  onClose: () => void
  lastReminder?: string
  canRemind: boolean
  search?: string
}> = ({ reviewId, onClose, lastReminder, canRemind, search }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const {
    employees,
    manager,
    onlyDirectReports,
    agreementStatus,
    scores,
    teamIds,
    tenureDates,
    workplaceIds,
    legalEntityIds,
  } = useFiltersValues()

  const { data, loading } = useQuery(PerformanceProcessPendingReviewersDocument, {
    variables: {
      reviewProcessId: reviewId,
      evaluationsStatus: 'pending',
      employeeIds: employees?.map((employee) => employee.id),
      managedByFilter: manager
        ? {
            managerEmployeeId: manager.id,
            onlyDirectReports: onlyDirectReports ?? true,
          }
        : undefined,
      search: search || undefined,
      agreementStatus,
      scores,
      teamIds,
      tenureDates,
      workplaceIds,
      legalEntityIds,
    },
  })

  // TODO: Add a repository for fetching the pending reviewers for a review process that also supports pagination
  const pendingReviewersIds = uniq(
    data?.performance.reviewProcessTargets.flatMap((target) =>
      target.unpublishedEvaluations.map((evaluation) => evaluation.reviewerAccessId).filter(Boolean)
    )
  )

  const [sendReminderMutation, { loading: sendingReminders }] = useMutation(
    SendRemindersPerformanceReviewProcessDocument,
    {
      extractorFn: ({ performanceMutations }) =>
        performanceMutations.remindInBulkPerformanceReviewProcess,
    }
  )

  const sendReminder = async () => {
    const response = await sendReminderMutation({
      variables: {
        id: Number(reviewId),
      },
    })

    const result = handleResult(response, () => null)
    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.overview.participation.send_reminders.success'),
      })
    }
    onClose?.()
    return result
  }

  return (
    <>
      <Sidepanel
        open
        onClose={() => onClose()}
        width="wideXL"
        title={i18n.t('performance_process.overview.participation.pending_reviewers')}
        footer={
          canRemind && (
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              {i18n.t('performance_process.overview.participation.send_reminders.title')}
            </Button>
          )
        }
      >
        <Box>
          {loading ? (
            <Skeleton isLoading height="full" />
          ) : (
            <PendingReviewersDataTable pendingReviewersIds={pendingReviewersIds} />
          )}
        </Box>
      </Sidepanel>
      {isModalOpen && (
        <Modal
          title={i18n.t('performance_process.overview.participation.send_reminders.modal.title')}
          description={
            <Box gap="s8">
              <Text size="300">
                {i18n.t(
                  'performance_process.overview.participation.send_reminders.modal.description'
                )}
              </Text>
              {lastReminder && (
                <Text size="300" weight="semibold">
                  {i18n.t(
                    'performance_process.overview.participation.send_reminders.modal.last_reminder'
                  )}
                  {customRelativeTime(moment(lastReminder))}{' '}
                </Text>
              )}
            </Box>
          }
          illustration={'alert'}
          onClose={() => setIsModalOpen(false)}
          footer={
            <>
              <Button
                type="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={sendingReminders}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={() => sendReminder()} disabled={sendingReminders}>
                {i18n.t('performance_process.overview.participation.send_reminders.modal.send')}
              </Button>
            </>
          }
        ></Modal>
      )}
    </>
  )
}
